import React, {useEffect, useRef, useState} from 'react';
import {Checkbox, Col, DatePicker, Form, Input, message, Row} from 'antd';
import type {ProFormInstance} from '@ant-design/pro-form';
import {ProForm} from '@ant-design/pro-form';
import {FieldProps, WdiCardTitle} from '@/components/WdiControls';
import type {FormPageProps} from '@/components/WdiBus/FormPage';
import {ApplyFooter, ApplyFooterType} from '@/components/WdiBus/ApplyFooter';
import {FileUploadUtility, WdiFileUpload} from '@/components/WdiControls/WdiFileUpload';
import {ResponseStatus} from '@/utils/request';
import type {ViewEntityProps} from '../../SalesContract/components/data';
import {getPageResult, initPageContext, isContractType1,} from '../../SalesContract/components/data';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import SalesContractTermsDetails from './details';
import {WdiSelect, WdiTreeSelect} from '@/components/WdiControls/WdiSelect';
import {SalesContract} from '@/services/contract/salescontract';
import moment from 'moment';
import {SalesContractTerms} from '@/services/contract/salescontractterms';
import DataStore from '@/components/GridWidget/DataStore';
import {Enum} from '@/enums/system';
import {SoldTo} from '@/services/mdm/soldto';
import {SoldToGroup} from '@/services/mdm/soldtogroup';
import {BOHQ} from '@/services/mdm/bohq';
import {BO} from '@/services/mdm/bo';
import {RebateInfo} from '@/services/contract/rebateinfo';
import {WdiEditor} from "@/components/WdiControls/WdiEditor";

const { RangePicker } = DatePicker;

const SalesContractApply: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();

  // 合同类型
  const [contractType, setContractType] = useState<number>();

  // 当前合同是否为编辑模式
  const [isEdit, setIsEdit] = useState<boolean | undefined>();
  const [hasRebateInfo, setHasRebateInfo] = useState<boolean>(false);

  const [entity, setEntity] = useState<ViewEntityProps>();

  const [dataSource, setDataSource] = useState<any[]>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandKeysBo, setExpandKeysBo] = useState<string[]>();
  const [expandKeysProductReachEx, setExpandKeysProductReachEx] = useState<string[]>();
  const [editorRef,setEditorRef] = useState<any>();

  const doSubmit = async (type: number, forceSubmit: boolean) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    const remark = await editorRef?.current?.getHtml?.();
    const values = await formRef.current?.validateFieldsReturnFormatValue?.();
    if (values && values.errorFields && values.errorFields.length) {
      setIsSubmitting(false);
      return;
    }
    values.remark = remark;
    const dataResult = getPageResult(values, entity, dataSource);
    props?.setLoading?.(true);
    if (dataResult.code !== ResponseStatus.SUCCESS) {
      message.error(dataResult.message);
      setIsSubmitting(false);
      return;
    }
    const response = await SalesContract.post({
      ...dataResult.data,
    });
    // 处理成功响应
    setIsSubmitting(false);
    return response;
  };

  const onDetailsChange = (data: any) => {
    setDataSource(data);
  };

  const soldToDataLoading = async (params: any) => {
    const response = await SoldTo.ajaxSearch(
      Object.assign(
        {
          isActive: true,
          withPermissions: true,
        },
        params,
      ),
    );
    return response.data?.map((item) => ({
      value: item.soldToCode,
      label: item.codeOrName,
    }));
  };

  const soldToGroupDataLoading = async (params: any) => {
    const response = await SoldToGroup.ajaxSearch(
      Object.assign(
        {
          isActive: true,
          withPermissions: true,
        },
        params,
      ),
    );
    return response.data?.map((item) => ({ value: item.soldToGroupId, label: item.codeOrName }));
  };

  const getHeaderInfo = () => {
    const data: any = {};
    if (formRef && formRef.current) {
      data.soldToCode = formRef.current?.getFieldValue('soldToCode');
    }
    if (formRef && formRef.current) {
      data.soldToGroupId = formRef.current?.getFieldValue('soldToGroupId');
    }
    return data;
  };

  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity = result.data;
        const { salesContract, pageTypeId } = viewEntity;
        if (pageTypeId == Enum.PageType.CONTRACT_APPLICATION) {
          setContractType(Enum.SalesContractType.SELLIN);
        } else {
          setContractType(Enum.SalesContractType.SELLOUT);
        }

        if (salesContract) {
          setIsEdit(true);
          setExpandKeysProductReachEx([String(salesContract.bohqId)]);

          formRef.current?.setFieldsValue({
            soldToCode: salesContract.soldToCode,
            soldToGroupId: salesContract.soldToGroupId,
            bohqId: salesContract.bohqId,
            contractCode: salesContract.contractCode,
            contractName: salesContract.contractName,
            contractDate: [moment(salesContract.beginDate), moment(salesContract.endDate)],
            owner: salesContract.owner,
            signDate: salesContract.signDate ? moment(salesContract.signDate) : null,
            customerSignatory: salesContract.customerSignatory,
            regionId: salesContract.regionId,
            remark: salesContract.remark,
            isAutoAppend: salesContract.isAutoAppend,
            fileInfo: FileUploadUtility.parse(salesContract.fileName, salesContract.filePath),
          });
          SalesContractTerms.getSalesContractTerms(salesContract.salesContractId).then(
            (response) => {
              setDataSource(DataStore.init(response.data));
            },
          );
          RebateInfo.getBySalesContractId(salesContract.salesContractId).then((res) => {
            if (res.data && res.data.length > 0) {
              setHasRebateInfo(true);
            }
          });
          if (salesContract.boIds) {
            BO.search({ boIds: salesContract.boIds }).then((response) => {
              const bos = response.data;
              setExpandKeysBo(bos.map((item) => String(item.boId)));
              formRef.current?.setFieldsValue({
                boIds: bos.map((item) => ({ value: item.boId, label: item.boName })),
              });
            });
          }
        }
        setEntity(viewEntity);
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && entity) {
      const { pageTypeId } = entity;
      let title = isContractType1(pageTypeId) ? '创建一级客户合同' : '创建二级客户合同';
      if (entity.salesContract) {
        title = isContractType1(pageTypeId) ? '编辑一级客户合同' : '编辑二级客户合同';
      }
      props.setPageTitle(title);
    }
  }, [props.setPageTitle, entity]);

  return (
    <>
      <ProForm
        formRef={formRef}
        submitter={{
          render: () => {
            return (
              <ApplyFooter
                setLoading={props?.setLoading}
                hiddenButton={[ApplyFooterType.Validate, ApplyFooterType.Draft]}
                viewEntity={entity}
                submitBackUrl={
                  contractType != null && contractType == Enum.SalesContractType.SELLIN
                    ? '/contract/salescontract'
                    : '/contract/salescontractindirect'
                }
                doSubmit={doSubmit}
              />
            );
          },
        }}
      >
        <WdiPanel title={<WdiCardTitle iconType={Enum.IconType.BASE_INFO} title="基本信息" />}>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={4}>
              <Form.Item name="soldToCode" label="客户">
                <WdiSelect
                  placeholder="请选择客户"
                  showSearch={true}
                  disabled={isEdit}
                  remote={true}
                  dataLoading={soldToDataLoading}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="soldToGroupId" label="返利客户组">
                <WdiSelect
                  placeholder="请选择返利客户组"
                  showSearch={true}
                  disabled={isEdit}
                  remote={true}
                  dataLoading={soldToGroupDataLoading}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                name="bohqId"
                label="预算归属"
                rules={[{required: true, message: '预算归属不能为空!'}]}
              >
                <WdiTreeSelect
                  {...{
                    placeholder: '请选择预算归属',
                    labelInValue: true,
                    defaultExpandKeys: expandKeysProductReachEx,
                    dataLoading: async (params) => {
                      const response = await BOHQ.search({
                        isActive: true,
                        withPermissions: true,
                        bohqLevels: [
                          Enum.BOLevel.BO1,
                          Enum.BOLevel.BO2,
                        ].join(),
                        existLessRoot: true
                      });
                      const {treeData, dict} = FieldProps.TreeSelect.formatTreeData(
                        {
                          textField: 'bohqName',
                          valueField: 'bohqId',
                          id: 'bohqId',
                          pId: 'parentId',
                          level: 'bohqLevel',
                          disabledLevel: [0, 1],
                        },
                        response.data,
                      );
                      return {treeData, dict};
                    },
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="boIds" label="使用部门范围(不填即全国)">
                <WdiTreeSelect
                  {...{
                    multiple: true,
                    placeholder: '请选择使用部门',
                    defaultExpandKeys: expandKeysBo,
                    // disabled: hasRebateInfo,
                    dataLoading: async (params) => {
                      const response = await BO.search(
                        Object.assign({ isActive: true, boLevels: '1,2,3,4' }, params),
                      );
                      const { treeData, dict } = FieldProps.TreeSelect.formatTreeData(
                        {
                          textField: 'boName',
                          valueField: 'boId',
                          id: 'boId',
                          pId: 'parentId',
                        },
                        response.data,
                      );
                      return { treeData, dict };
                    },
                    onExSelect: (value: any, node: any, dict: any) => {
                      const values = formRef.current?.getFieldValue('boIds');
                      const rtv = FieldProps.TreeSelect.labeledValuesMeger(dict, values, { value });
                      formRef.current?.setFieldsValue({
                        boIds: rtv,
                      });
                    },
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="contractCode" label="合同编号">
                <Input />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="contractName" label="合同名称">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={8}>
              <Form.Item
                required
                name="contractDate"
                label="合同期间"
                rules={[{ required: true, message: '合同期间不能为空!' }]}
              >
                <RangePicker picker={'month'} format={'YYYY-MM'} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="owner" label="合同负责人">
                <Input />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="customerSignatory" label="客户方签约人">
                <Input />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="signDate" label="签约日期">
                <DatePicker />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="isAutoAppend" label="是否自动追溯" valuePropName="checked">
                <Checkbox />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={16}>
              <Form.Item
                name="remark"
                label="合同备注"
                rules={[{ required: false, message: '合同备注不能为空!' }]}
              >
                <WdiEditor setRef={setEditorRef} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item
                name="fileInfo"
                label="附件"
                valuePropName="fileList"
                getValueFromEvent={(e: any) => {
                  return e.fileList || [];
                }}
              >
                <WdiFileUpload leftTopInfo="请选择文件并上传(单个文件不能超500M)" multiple={true}/>
              </Form.Item>
            </Col>
          </Row>
        </WdiPanel>
        {entity?.pageTypeId && (
          <SalesContractTermsDetails
            pid={entity?.salesContractId}
            pageTypeId={entity?.pageTypeId}
            getHeaderInfo={getHeaderInfo}
            dataSource={dataSource}
            onChange={onDetailsChange}
          />
        )}
      </ProForm>
    </>
  );
};
export default SalesContractApply;
